import { useCallback, useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { http } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { ArrowLeft, Ticket, Plus, Trash2, Power, Users, Copy, X, Download, BarChart3 } from 'lucide-react';

const STATUS_STYLE = {
  active: 'bg-green-100 text-green-700',
  disabled: 'bg-navy/10 text-navy/60',
  expired: 'bg-amber-100 text-amber-700',
  exhausted: 'bg-red-100 text-red-700',
};

const toCSV = (rows) => {
  const headers = ['code', 'status', 'access', 'description', 'used', 'max_redemptions', 'redeem_by', 'created_at'];
  const esc = (v) => { const s = v == null ? '' : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  const d = (iso) => { if (!iso) return ''; try { return new Date(iso).toISOString().slice(0, 10); } catch { return ''; } };
  const lines = [headers.join(',')];
  rows.forEach((c) => lines.push([c.code, c.status || '', c.access_label || 'forever', c.description || '',
    c.redeemed_count ?? 0, c.max_redemptions ?? '', d(c.expires_at), d(c.created_at)].map(esc).join(',')));
  return lines.join('\n');
};

const downloadCSV = (rows, filename) => {
  const blob = new Blob([toCSV(rows)], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
};

export default function AdminPromoCodes() {
  const { user } = useAuth();
  const [codes, setCodes] = useState(null);
  const [error, setError] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ quantity: 1, code: '', description: '', max_redemptions: '', expires_at: '', access_type: 'forever', access_days: '', access_until: '', kind: 'free', discount_type: 'percent', discount_value: 25, discount_duration: 'first_cycle' });
  const [redemptionsFor, setRedemptionsFor] = useState(null);
  const [redemptions, setRedemptions] = useState(null);
  const [lastBatch, setLastBatch] = useState(null);
  const [stats, setStats] = useState(null);

  const load = useCallback(() => {
    http.get('/admin/promo-codes')
      .then((r) => setCodes(r.data?.codes || []))
      .catch(() => setError(true));
    http.get('/admin/promo-codes/stats').then((r) => setStats(r.data)).catch(() => {});
  }, []);

  useEffect(() => { if (user?.is_admin) load(); }, [user, load]);

  if (!user?.is_admin) return <Navigate to="/app" replace />;

  const fmtDate = (iso) => { if (!iso) return '—'; try { return new Date(iso).toLocaleDateString(); } catch { return iso; } };

  const create = async () => {
    setCreating(true);
    try {
      const body = {
        quantity: Number(form.quantity) || 1,
        description: form.description || '',
        max_redemptions: form.max_redemptions === '' ? null : Number(form.max_redemptions),
        expires_at: form.expires_at || null,
        access_type: form.access_type,
        access_days: form.access_type === 'days' ? (Number(form.access_days) || null) : null,
        access_until: form.access_type === 'until' ? (form.access_until || null) : null,
        kind: form.kind,
        discount_type: form.kind === 'discount' ? form.discount_type : null,
        discount_value: form.kind === 'discount' ? Number(form.discount_value) : null,
        discount_duration: form.kind === 'discount' ? form.discount_duration : null,
      };
      if (Number(form.quantity) === 1 && form.code.trim()) body.code = form.code.trim();
      const r = await http.post('/admin/promo-codes', body);
      const made = r.data?.created || [];
      setLastBatch(made);
      toast.success(made.length > 1 ? `${made.length} codes created.` : `Code ${made[0]?.code} created.`);
      setForm({ quantity: 1, code: '', description: '', max_redemptions: '', expires_at: '', access_type: 'forever', access_days: '', access_until: '', kind: 'free', discount_type: 'percent', discount_value: 25, discount_duration: 'first_cycle' });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create code(s).');
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (c) => {
    try { await http.patch(`/admin/promo-codes/${c.id}`, { active: !c.active }); load(); }
    catch { toast.error('Could not update the code.'); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete promo code ${c.code}? Redemption history is kept.`)) return;
    try { await http.delete(`/admin/promo-codes/${c.id}`); toast.success('Code deleted.'); load(); }
    catch { toast.error('Could not delete the code.'); }
  };

  const viewRedemptions = async (c) => {
    setRedemptionsFor(c); setRedemptions(null);
    try { const r = await http.get(`/admin/promo-codes/${c.id}/redemptions`); setRedemptions(r.data?.redemptions || []); }
    catch { setRedemptions([]); }
  };

  const copy = (code) => { try { navigator.clipboard.writeText(code); toast.success(`Copied ${code}`); } catch { /* ignore */ } };

  return (
    <div className="p-6 lg:px-10 lg:pt-10 space-y-6" data-testid="admin-promo">
      <Link to="/app/admin" data-testid="admin-promo-back" className="inline-flex items-center gap-2 text-sm text-navy/60 hover:text-navy">
        <ArrowLeft size={14}/> Back to admin
      </Link>
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">Admin</p>
        <h1 className="font-heading text-3xl mt-1">Promo Codes</h1>
        <p className="text-navy/50 text-sm mt-1">100%-off comp / beta codes. A redeemed code unlocks membership instantly — no PayPal, no charge.</p>
      </div>

      {/* Redemption dashboard */}
      {stats && (
        <div className="bg-white rounded-3xl p-6 card-soft" data-testid="admin-promo-stats">
          <p className="font-heading text-lg mb-4 flex items-center gap-2"><BarChart3 size={18} className="text-gold"/> Redemptions</p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="rounded-2xl bg-navy/5 p-4" data-testid="stat-total-codes">
              <p className="font-heading text-3xl text-navy">{stats.total_codes}</p>
              <p className="text-xs text-navy/55 mt-1">Codes handed out</p>
            </div>
            <div className="rounded-2xl bg-navy/5 p-4" data-testid="stat-redeemed-codes">
              <p className="font-heading text-3xl text-navy">{stats.redeemed_codes}</p>
              <p className="text-xs text-navy/55 mt-1">Codes redeemed</p>
            </div>
            <div className="rounded-2xl bg-navy/5 p-4" data-testid="stat-total-redemptions">
              <p className="font-heading text-3xl text-navy">{stats.total_redemptions}</p>
              <p className="text-xs text-navy/55 mt-1">Total redemptions</p>
            </div>
            <div className="rounded-2xl bg-gold/15 p-4" data-testid="stat-redemption-rate">
              <p className="font-heading text-3xl text-navy">{stats.redemption_rate}%</p>
              <p className="text-xs text-navy/55 mt-1">Redemption rate</p>
            </div>
          </div>
          {/* Handed-out vs redeemed bar */}
          <div className="mt-4">
            <div className="flex justify-between text-xs text-navy/55 mb-1">
              <span>{stats.redeemed_codes} redeemed</span>
              <span>{stats.unused_codes} still unused</span>
            </div>
            <div className="h-3 w-full rounded-full bg-navy/10 overflow-hidden" data-testid="stat-bar">
              <div className="h-full bg-gold rounded-full transition-all"
                style={{ width: `${stats.total_codes ? (stats.redeemed_codes / stats.total_codes) * 100 : 0}%` }} />
            </div>
          </div>
          {/* 14-day mini bar chart */}
          {stats.daily && stats.daily.some((d) => d.count > 0) && (
            <div className="mt-5" data-testid="stat-daily">
              <p className="text-xs text-navy/55 mb-2">Redemptions · last 14 days</p>
              <div className="flex items-end gap-1 h-20">
                {stats.daily.map((d) => {
                  const max = Math.max(1, ...stats.daily.map((x) => x.count));
                  return (
                    <div key={d.date} className="flex-1 flex flex-col items-center justify-end h-full" title={`${d.date}: ${d.count}`}>
                      <div className="w-full bg-navy/70 rounded-t" style={{ height: `${(d.count / max) * 100}%`, minHeight: d.count ? 3 : 0 }} />
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Create */}
      <div className="bg-white rounded-3xl p-6 card-soft" data-testid="admin-promo-create">
        <p className="font-heading text-lg mb-3 flex items-center gap-2"><Plus size={18} className="text-gold"/> Create codes</p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-navy/50">Quantity</label>
            <input type="number" min="1" max="200" value={form.quantity}
              onChange={(e) => setForm({ ...form, quantity: e.target.value })}
              className="mt-1 w-full rounded-xl border border-navy/15 px-3 py-2 text-sm" data-testid="promo-quantity"/>
          </div>
          <div>
            <label className="text-xs text-navy/50">Custom code (only if quantity = 1)</label>
            <input value={form.code} disabled={Number(form.quantity) !== 1}
              onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
              placeholder="Auto-generated if blank"
              className="mt-1 w-full rounded-xl border border-navy/15 px-3 py-2 text-sm uppercase disabled:bg-navy/5" data-testid="promo-code"/>
          </div>
          <div>
            <label className="text-xs text-navy/50">Max redemptions / code (blank = unlimited)</label>
            <input type="number" min="1" value={form.max_redemptions}
              onChange={(e) => setForm({ ...form, max_redemptions: e.target.value })}
              placeholder="Unlimited"
              className="mt-1 w-full rounded-xl border border-navy/15 px-3 py-2 text-sm" data-testid="promo-max"/>
          </div>
          <div>
            <label className="text-xs text-navy/50">Redeem-by date (blank = never)</label>
            <input type="date" value={form.expires_at}
              onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
              className="mt-1 w-full rounded-xl border border-navy/15 px-3 py-2 text-sm" data-testid="promo-expiry"/>
          </div>
          <div>
            <label className="text-xs text-navy/50">Code type</label>
            <select value={form.kind}
              onChange={(e) => setForm({ ...form, kind: e.target.value })}
              className="mt-1 w-full rounded-xl border border-navy/15 px-3 py-2 text-sm bg-white" data-testid="promo-kind">
              <option value="free">Free access (100% off)</option>
              <option value="discount">Discount (% or $ off via PayPal)</option>
            </select>
          </div>
          {form.kind === 'discount' && (
            <>
              <div>
                <label className="text-xs text-navy/50">Discount type</label>
                <select value={form.discount_type}
                  onChange={(e) => setForm({ ...form, discount_type: e.target.value, discount_value: e.target.value === 'percent' ? 25 : 5 })}
                  className="mt-1 w-full rounded-xl border border-navy/15 px-3 py-2 text-sm bg-white" data-testid="promo-discount-type">
                  <option value="percent">Percent off (%)</option>
                  <option value="amount">Dollar off ($)</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-navy/50">Amount</label>
                <select value={form.discount_value}
                  onChange={(e) => setForm({ ...form, discount_value: Number(e.target.value) })}
                  className="mt-1 w-full rounded-xl border border-navy/15 px-3 py-2 text-sm bg-white" data-testid="promo-discount-value">
                  {(form.discount_type === 'percent' ? [10, 25, 50, 75] : [5, 10]).map((v) => (
                    <option key={v} value={v}>{form.discount_type === 'percent' ? `${v}% off` : `$${v} off`}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-navy/50">Applies to</label>
                <select value={form.discount_duration}
                  onChange={(e) => setForm({ ...form, discount_duration: e.target.value })}
                  className="mt-1 w-full rounded-xl border border-navy/15 px-3 py-2 text-sm bg-white" data-testid="promo-discount-duration">
                  <option value="first_cycle">First payment only</option>
                  <option value="lifetime">Every renewal (lifetime)</option>
                </select>
              </div>
              <div className="lg:col-span-2">
                <p className="text-[11px] text-navy/45">Discount codes are entered on the web checkout page and reduce the real PayPal price. They don't grant free access.</p>
              </div>
            </>
          )}
          {form.kind === 'free' && (<>
          <div>
            <label className="text-xs text-navy/50">Access window (how long membership lasts)</label>
            <select value={form.access_type}
              onChange={(e) => setForm({ ...form, access_type: e.target.value })}
              className="mt-1 w-full rounded-xl border border-navy/15 px-3 py-2 text-sm bg-white" data-testid="promo-access-type">
              <option value="forever">Forever (comp)</option>
              <option value="days">Number of days</option>
              <option value="until">Until a date</option>
            </select>
          </div>
          {form.access_type === 'days' && (
            <div>
              <label className="text-xs text-navy/50">Free access for (days)</label>
              <input type="number" min="1" value={form.access_days}
                onChange={(e) => setForm({ ...form, access_days: e.target.value })}
                placeholder="e.g. 30"
                className="mt-1 w-full rounded-xl border border-navy/15 px-3 py-2 text-sm" data-testid="promo-access-days"/>
            </div>
          )}
          {form.access_type === 'until' && (
            <div>
              <label className="text-xs text-navy/50">Free access until</label>
              <input type="date" value={form.access_until}
                onChange={(e) => setForm({ ...form, access_until: e.target.value })}
                className="mt-1 w-full rounded-xl border border-navy/15 px-3 py-2 text-sm" data-testid="promo-access-until"/>
            </div>
          )}
          </>)}
          <div className="lg:col-span-2">
            <label className="text-xs text-navy/50">Description / note</label>
            <input value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="e.g. Beta tester batch"
              className="mt-1 w-full rounded-xl border border-navy/15 px-3 py-2 text-sm" data-testid="promo-desc"/>
          </div>
        </div>
        <button onClick={create} disabled={creating}
          className="mt-4 rounded-xl bg-navy text-white text-sm font-semibold px-5 py-2.5 disabled:opacity-50" data-testid="promo-create-btn">
          {creating ? 'Creating…' : 'Create'}
        </button>
        {lastBatch && lastBatch.length > 0 && (
          <div className="mt-4 flex flex-wrap items-center gap-3 rounded-xl bg-green-50 border border-green-200 px-4 py-3" data-testid="promo-batch-banner">
            <span className="text-sm text-green-800">{lastBatch.length} code{lastBatch.length > 1 ? 's' : ''} ready to hand out.</span>
            <button onClick={() => downloadCSV(lastBatch, `promo-codes-batch-${new Date().toISOString().slice(0, 10)}.csv`)}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-green-800 hover:text-green-900" data-testid="promo-download-batch">
              <Download size={14}/> Download this batch (CSV)
            </button>
            <button onClick={() => setLastBatch(null)} className="text-green-700/60 hover:text-green-900 ml-auto"><X size={15}/></button>
          </div>
        )}
      </div>

      {error && <div className="bg-white rounded-3xl p-6 card-soft text-navy/60" data-testid="admin-promo-error">Couldn&apos;t load promo codes. <button onClick={load} className="underline">Retry</button></div>}
      {!error && codes === null && <div className="text-navy/50" data-testid="admin-promo-loading">Loading…</div>}
      {!error && codes && codes.length === 0 && (
        <div className="bg-white rounded-3xl p-10 card-soft text-center text-navy/50" data-testid="admin-promo-empty">No promo codes yet. Create your first above.</div>
      )}

      {!error && codes && codes.length > 0 && (
        <div className="bg-white rounded-3xl p-6 card-soft" data-testid="admin-promo-list">
          <div className="flex items-center justify-between mb-3">
            <p className="font-heading text-lg flex items-center gap-2"><Ticket size={18} className="text-gold"/> Codes ({codes.length})</p>
            <button onClick={() => downloadCSV(codes, `promo-codes-all-${new Date().toISOString().slice(0, 10)}.csv`)}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-navy/70 hover:text-navy rounded-lg px-3 py-1.5 hover:bg-navy/5" data-testid="promo-export-all">
              <Download size={14}/> Export all (CSV)
            </button>
          </div>
          <div className="divide-y divide-navy/5">
            {codes.map((c) => (
              <div key={c.id} className="py-3 flex flex-wrap items-center justify-between gap-3" data-testid={`promo-row-${c.code}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-semibold text-navy">{c.code}</span>
                    <button onClick={() => copy(c.code)} className="text-navy/40 hover:text-navy" title="Copy" data-testid={`promo-copy-${c.code}`}><Copy size={13}/></button>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${STATUS_STYLE[c.status] || 'bg-navy/10 text-navy/60'}`}>{c.status}</span>
                  </div>
                  <p className="text-xs text-navy/50 mt-0.5">
                    {c.description ? `${c.description} · ` : ''}
                    Used {c.redeemed_count}{c.max_redemptions != null ? ` / ${c.max_redemptions}` : ''}
                    {` · access ${c.access_label || 'forever'}`}
                    {c.expires_at ? ` · redeem by ${fmtDate(c.expires_at)}` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => viewRedemptions(c)} className="inline-flex items-center gap-1 text-xs text-navy/60 hover:text-navy px-2 py-1 rounded-lg hover:bg-navy/5" data-testid={`promo-redemptions-${c.code}`}><Users size={13}/> Uses</button>
                  <button onClick={() => toggleActive(c)} className="inline-flex items-center gap-1 text-xs text-navy/60 hover:text-navy px-2 py-1 rounded-lg hover:bg-navy/5" data-testid={`promo-toggle-${c.code}`}><Power size={13}/> {c.active ? 'Disable' : 'Enable'}</button>
                  <button onClick={() => remove(c)} className="inline-flex items-center gap-1 text-xs text-red-500 hover:text-red-700 px-2 py-1 rounded-lg hover:bg-red-50" data-testid={`promo-delete-${c.code}`}><Trash2 size={13}/> Delete</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Redemptions drawer */}
      {redemptionsFor && (
        <div className="fixed inset-0 bg-navy/40 flex items-center justify-center p-4 z-50" onClick={() => setRedemptionsFor(null)} data-testid="promo-redemptions-modal">
          <div className="bg-white rounded-3xl p-6 max-w-md w-full max-h-[80vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <p className="font-heading text-lg">Redemptions · {redemptionsFor.code}</p>
              <button onClick={() => setRedemptionsFor(null)} className="text-navy/40 hover:text-navy"><X size={18}/></button>
            </div>
            {redemptions === null && <p className="text-navy/50 text-sm">Loading…</p>}
            {redemptions && redemptions.length === 0 && <p className="text-navy/50 text-sm">Not redeemed yet.</p>}
            {redemptions && redemptions.length > 0 && (
              <div className="divide-y divide-navy/5">
                {redemptions.map((r) => (
                  <div key={r.id} className="py-2 flex items-center justify-between text-sm">
                    <span className="text-navy">{r.user_email || r.user_id}</span>
                    <span className="text-xs text-navy/50">{fmtDate(r.redeemed_at)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
